import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useRequest } from '@frontend/api';

import { MFG_ITEMS_SUBQUERY, MFG_QUERY_KEYS } from './constants';
import type {
  AdskMfgQueryResource,
  FusionItem,
  AdskMfgParams,
  AdskMfgQueryResponse,
  AdskMfgInfiniteQueryFunction,
} from './types';
import { adskRequest, isAdskMfgError } from './utils';

const GRAPHQL_QUERY = {
  operationName: 'GetItemsByProject',
  query: `query GetItemsByProject($projectId:ID!, $pagination: PaginationInput) {
        project(projectId: $projectId) {
            items(pagination: $pagination) {
                pagination {
                    cursor
                }
                results ${MFG_ITEMS_SUBQUERY}
            }
        }
    }`,
};

type DataType = { items: AdskMfgQueryResource<FusionItem[]> };
type ReturnType = {
  project: DataType;
};

type Params = AdskMfgParams & {
  projectId: string | null;
};

type ListItemsByProjectQueryKey = readonly [
  typeof MFG_QUERY_KEYS.LIST_ITEMS_BY_PROJECT,
  string,
];

export const getListItemsByProjectQueryKey = (
  projectId: string,
): ListItemsByProjectQueryKey => [
  MFG_QUERY_KEYS.LIST_ITEMS_BY_PROJECT,
  projectId,
];

export const useListItemsByProject: AdskMfgInfiniteQueryFunction<
  AdskMfgQueryResponse<ReturnType>,
  [Params],
  ListItemsByProjectQueryKey,
  FusionItem
> = (
  { accessToken, refreshAccess, projectId, pagination = { cursor: null } },
  { queryOptions } = {},
) => {
  const queryClient = useQueryClient();
  const request = useRequest();
  const results = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- adskRequest should be built with a hook, pagination should be added to the query key
    queryKey: getListItemsByProjectQueryKey(projectId ?? ''),
    queryFn: ({ signal, pageParam }) =>
      adskRequest<DataType, 'project'>(
        accessToken ?? '',
        {
          ...GRAPHQL_QUERY,
          variables: {
            projectId,
            pagination: { ...pagination, cursor: pageParam },
          },
        },
        request,
        { init: { signal } },
        (data) => !!data.project && !!data.project.items,
        (msg) => `Failed to list items for project ${projectId}: ${msg}`,
      ),
    enabled: !!accessToken && !!projectId,
    getNextPageParam: (currentPage) =>
      currentPage.data.project.items.pagination.cursor === null
        ? null
        : currentPage.data.project.items.pagination.cursor,
    ...queryOptions,
    onError: async (error) => {
      // If not an ADSK error, bail
      if (!isAdskMfgError(error)) return;

      // Trigger refresh on 401s
      if (error.statusCode === 401) {
        await refreshAccess();

        // Invalidate query to trigger a new request
        queryClient.invalidateQueries({
          queryKey: getListItemsByProjectQueryKey(projectId ?? ''),
        });
        return;
      }

      // Run the passed in onError if provided
      queryOptions?.onError?.(error);
    },
  });

  const flatData = useMemo(
    () =>
      results?.data?.pages?.flatMap((page) => page.data.project.items.results),
    [results?.data?.pages],
  );

  const flatErrors = useMemo(
    () => results?.data?.pages?.flatMap((page) => page?.errors ?? []),
    [results?.data?.pages],
  );

  return { ...results, flatData, flatErrors };
};
